/**
 * The projection engine's vocabulary — input model, output projection, warnings.
 *
 * These types are the only description of a scenario that both runtimes agree
 * on: the browser holds one in the editor's draft state, the server rebuilds one
 * from Prisma rows before recomputing the summary columns, and a
 * `ScenarioVersion` snapshot is one of these serialised verbatim.
 *
 * The enums are written out as string unions rather than imported from
 * `@prisma/client` because src/shared has to run in a browser. The pairs are
 * kept honest by tests/shared/roi.enums.test.ts, exactly as `appRoleSchema` is.
 *
 * Money is a plain `number` in whole currency units; the Decimal ⇄ number
 * boundary lives in src/server/lib/decimal.ts and is crossed before anything
 * reaches here. See docs/03-domain-model.md and docs/04-projection-engine.md.
 */

/* ------------------------------------------------------------------- enums */

export type BillingModel = "SUBSCRIPTION" | "PERPETUAL" | "HYBRID";
export type RecurringPeriod = "MONTHLY" | "QUARTERLY" | "ANNUAL";
export type MaintenanceMode = "NONE" | "PERCENT_OF_LICENCE" | "FLAT";
export type LineDirection = "COST" | "REVENUE";
export type LineGroup = "BUILD" | "INFRASTRUCTURE" | "OPERATIONS" | "ADDON" | "SERVICES" | "OTHER";
export type LineBasis =
  | "FIXED_ONE_TIME"
  | "FIXED_PER_YEAR"
  | "PER_ACTIVE_CLIENT_YEAR"
  | "PER_NEW_CLIENT_ONCE"
  | "PER_ACTIVE_UNIT_YEAR"
  | "PCT_OF_REVENUE"
  | "PCT_OF_COST";
export type ChurnMode = "COUNT" | "RATE";

export const BILLING_MODELS = ["SUBSCRIPTION", "PERPETUAL", "HYBRID"] as const;
export const RECURRING_PERIODS = ["MONTHLY", "QUARTERLY", "ANNUAL"] as const;
export const MAINTENANCE_MODES = ["NONE", "PERCENT_OF_LICENCE", "FLAT"] as const;
export const LINE_DIRECTIONS = ["COST", "REVENUE"] as const;
export const LINE_GROUPS = [
  "BUILD",
  "INFRASTRUCTURE",
  "OPERATIONS",
  "ADDON",
  "SERVICES",
  "OTHER",
] as const;
export const LINE_BASES = [
  "FIXED_ONE_TIME",
  "FIXED_PER_YEAR",
  "PER_ACTIVE_CLIENT_YEAR",
  "PER_NEW_CLIENT_ONCE",
  "PER_ACTIVE_UNIT_YEAR",
  "PCT_OF_REVENUE",
  "PCT_OF_COST",
] as const;
export const CHURN_MODES = ["COUNT", "RATE"] as const;

/**
 * The bases whose amount is a percentage rather than an amount of money. They
 * are also the two the engine evaluates last, so they never feed each other —
 * see docs/04-projection-engine.md#ordering.
 */
export const PERCENTAGE_BASES = ["PCT_OF_REVENUE", "PCT_OF_COST"] as const;

/* ------------------------------------------------------------------- input */

/**
 * One sellable tier. Which money fields the engine reads depends on
 * `billingModel` — see the table in docs/03-domain-model.md. The unread fields
 * keep their stored values so switching billing model back and forth in the UI
 * is lossless.
 */
export interface PackageInput {
  id: string;
  name: string;
  sortOrder: number;
  colorIndex: number;

  billingModel: BillingModel;
  /** Per period, per active client. Ignored when `billingModel` is PERPETUAL. */
  recurringPrice: number;
  /** Per period, per active client. Ignored when `billingModel` is PERPETUAL. */
  recurringCost: number;
  recurringPeriod: RecurringPeriod;
  /** Charged at signing: a setup fee, or the perpetual licence fee. */
  oneTimePrice: number;
  /** Incurred at signing: onboarding labour. */
  oneTimeCost: number;
  /** Capex at signing; returned to the pool on churn when `reuseAssets`. */
  assetCost: number;

  /** All five are ignored when `billingModel` is SUBSCRIPTION. */
  maintenanceMode: MaintenanceMode;
  maintenanceRatePct: number;
  maintenanceFlatAmount: number;
  maintenanceStartYear: number;
  maintenanceAttachPct: number;
}

/** A cost or a revenue that is not attached to a package's price list. */
export interface LineItemInput {
  id: string;
  name: string;
  direction: LineDirection;
  group: LineGroup;
  basis: LineBasis;
  /** Money, or a percentage when `basis` is one of {@link PERCENTAGE_BASES}. */
  amount: number;
  unitsPerClient: number;
  attachPct: number;
  startYear: number;
  /** `null` runs it to the end of the horizon. */
  endYear: number | null;
  /** Empty restricts nothing — the item multiplies the whole client base. */
  packageIds: string[];
  sortOrder: number;
}

export interface YearInput {
  newClients: number;
  /** packageId → clients signed. Keys not naming a package are ignored. */
  mix: Record<string, number>;
  churnMode: ChurnMode;
  churnCount: number;
  churnRatePct: number;
  note?: string;
}

export interface ScenarioModel {
  currencyCode: string;
  /** 2..10, and always `=== years.length`. */
  horizonYears: number;
  /** 0..5. A cohort signed in year Y first becomes eligible in Y + this. */
  commitmentYears: number;
  /** 0 disables NPV discounting. */
  discountRatePct: number;
  reuseAssets: boolean;
  packages: PackageInput[];
  lineItems: LineItemInput[];
  years: YearInput[];
}

/* ------------------------------------------------------------------ output */

export interface PackageYearBreakdown {
  recurringRevenue: number;
  oneTimeRevenue: number;
  maintenanceRevenue: number;
  revenue: number;
  recurringCost: number;
  oneTimeCost: number;
  assetCapex: number;
  cost: number;
  net: number;
}

export interface YearResult {
  year: number;

  // client movement
  newByPackage: Record<string, number>;
  newTotal: number;
  /** `newClients` the mix did not allocate to any package. Earns nothing. */
  unassigned: number;
  churnByPackage: Record<string, number>;
  churnTotal: number;
  /** What the year asked for, before the commitment clipped it. */
  churnRequested: number;
  /** What the commitment permitted. */
  churnEligible: number;
  /** Clients still paying — subscription seats and un-lapsed maintenance. */
  activeByPackage: Record<string, number>;
  activeTotal: number;
  /** Perpetual and hybrid licences ever sold, lapsed ones included. */
  installedByPackage: Record<string, number>;
  installedTotal: number;

  // revenue
  recurringRevenue: number;
  oneTimeRevenue: number;
  maintenanceRevenue: number;
  lineItemRevenue: number;
  revenue: number;

  // cost
  recurringCost: number;
  oneTimeCost: number;
  assetCapex: number;
  assetsBought: number;
  assetsReused: number;
  lineItemCost: number;
  cost: number;

  net: number;
  cumRevenue: number;
  cumCost: number;
  cumNet: number;
  discountFactor: number;
  discountedNet: number;
  cumDiscountedNet: number;

  byPackage: Record<string, PackageYearBreakdown>;
  /** lineItemId → signed contribution: positive revenue, negative cost. */
  byLineItem: Record<string, number>;
}

export type ProjectionWarningCode =
  | "UNASSIGNED_CLIENTS"
  | "CHURN_CLIPPED"
  | "NO_PACKAGES"
  | "DEAD_PACKAGE"
  | "PERPETUAL_NO_MAINTENANCE"
  | "NEGATIVE_FOREVER"
  | "EMPTY_HORIZON";

/**
 * The engine reports rather than throws, so a half-built scenario still renders
 * its numbers. `message` is plain English the UI can show unmodified; `year`,
 * `packageId` and `lineItemId` let it link the warning to the input that caused it.
 */
export interface ProjectionWarning {
  code: ProjectionWarningCode;
  message: string;
  year?: number;
  packageId?: string;
  lineItemId?: string;
}

export interface Projection {
  rows: YearResult[];
  cumRevenue: number;
  cumCost: number;
  cumNet: number;
  /** `cumNet / cumCost * 100`, or 0 when nothing was spent. */
  roiPct: number;
  /** Interpolated within the year; `null` when it never happens in the horizon. */
  breakEvenYears: number | null;
  /** `null` when `discountRatePct` is 0 — an undiscounted NPV is just `cumNet`. */
  npv: number | null;
  /** `null` unless asked for, and when the net series never changes sign. */
  irrPct: number | null;
  endingActive: number;
  endingInstalled: number;
  /** Annualised recurring + maintenance revenue in the final year. */
  endingArr: number;
  warnings: ProjectionWarning[];
}

export interface ProjectionOptions {
  /**
   * Compute {@link Projection.irrPct}. Off by default: the bisection is the one
   * part of the engine that iterates, and the UI hides the tile rather than
   * show a misleading zero. See docs/04-projection-engine.md.
   */
  includeIrr?: boolean;
}
